import { ShopperAgentChannelKind } from "@redotech/crdb-enums/shopper-ai/shopper-agent-channel-kind";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { ShopperAgentMessageStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import {
  shopperAgentMessageChannelSchema,
  type ShopperAgentMessageChannelKind,
} from "@redotech/redo-flows-common/shopper-agent-message-channel";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import {
  Layout,
  RedoCheckboxCard,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox-card";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import {
  InspectorField,
  InspectorLayout,
  InspectorSection,
} from "@redotech/redo-web/inspector-v2";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { InvalidStepsList } from "../components/invalid-steps-list";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../steps";
import {
  shopperAgentMessageChannelForKind,
  shopperAgentMessageChannelWithExtendedOutreach,
  shopperAgentMessageChannelWithSubscribers,
} from "./shopper-agent-message-channel-state";

const ShopperAgentMessageInspector = memo(function ShopperAgentMessageInspector(
  props: StepTypeDetailsProps<PartialWithRequiredType<ShopperAgentMessageStep>>,
) {
  const { invalidSteps, readonly, save, setSelectedId, setStep, step } = props;
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [creatingStrategy, setCreatingStrategy] = useState(false);
  const isManagedAutomation = location.pathname.includes(
    "/marketing/automations/managed/",
  );
  const extendedOutreachEnabled = team.settings.recover?.enabled === true;
  const channelKind: ShopperAgentMessageChannelKind | undefined =
    step.channel?.kind;
  const channelValid = shopperAgentMessageChannelSchema.safeParse(
    step.channel,
  ).success;
  const otherInvalidSteps =
    invalidSteps?.filter(({ id }) => id !== step.id) ?? [];
  const textSelected = channelKind === ShopperAgentChannelKind.Sms;
  const extendedOutreachOn =
    textSelected && step.channel?.kind === ShopperAgentChannelKind.Sms
      ? step.channel.extendedOutreach === true
      : false;
  const subscribersOn =
    textSelected &&
    (step.channel?.kind !== ShopperAgentChannelKind.Sms ||
      step.channel.subscribers !== false);

  const setChannelKind = useHandler((kind: ShopperAgentMessageChannelKind) => {
    setStep({
      ...step,
      channel: shopperAgentMessageChannelForKind(kind, step.channel),
    });
  });

  const setExtendedOutreach = useHandler((extendedOutreach: boolean) => {
    setStep({
      ...step,
      channel: shopperAgentMessageChannelWithExtendedOutreach(
        step.channel,
        extendedOutreach,
      ),
    });
  });

  const setSubscribers = useHandler((subscribers: boolean) => {
    setStep({
      ...step,
      channel: shopperAgentMessageChannelWithSubscribers(
        step.channel,
        subscribers,
      ),
    });
  });

  const openStrategy = useHandler((strategyId: string) => {
    const search = new URLSearchParams({
      returnPath: location.pathname,
      selected: step.id,
    });
    navigate(
      `/stores/${team._id}/commerce-agent/strategies/${strategyId}?${search}`,
    );
  });

  const createStrategy = useHandler(async () => {
    setCreatingStrategy(true);
    try {
      const { strategyId } =
        await marketingRpcClient.createShopperAgentStrategy({});
      setStep({ ...step, strategyId });
      save?.();
      openStrategy(strategyId);
    } catch (error) {
      toast(
        t("Unable to create {{productName}} strategy: {{error}}", {
          productName: "Commerce Agent",
          error: String(error),
        }),
        { variant: "error" },
      );
    } finally {
      setCreatingStrategy(false);
    }
  });

  return (
    <InspectorLayout title={t("Start Agent Conversation")}>
      <InspectorSection label={t("Channel")}>
        <InspectorField label={null}>
          <Flex dir="column" gap="sm" w="full">
            <RedoCheckboxCard
              checked={textSelected}
              disabled={readonly}
              inputSize="sm"
              inputType="radio"
              layout={Layout.SHORT}
              onClick={() => {
                if (!textSelected) {
                  setChannelKind(ShopperAgentChannelKind.Sms);
                }
              }}
              title={t("Text")}
            >
              {textSelected && (
                <Flex
                  dir="column"
                  gap="md"
                  onClick={(event) => event.stopPropagation()}
                  w="full"
                >
                  <RedoCheckbox
                    alignToLabel
                    description={t(
                      "Send texts to customers subscribed to marketing.",
                    )}
                    disabled={
                      readonly || (!extendedOutreachEnabled && subscribersOn)
                    }
                    label={t("Subscribers")}
                    onClick={(event) => event.stopPropagation()}
                    setValue={(value) => {
                      if (value === true) {
                        setSubscribers(true);
                      } else if (extendedOutreachEnabled) {
                        setSubscribers(false);
                      }
                    }}
                    size="sm"
                    value={subscribersOn}
                  />
                  {(extendedOutreachEnabled || extendedOutreachOn) && (
                    <RedoCheckbox
                      alignToLabel
                      description={t(
                        "A human sends to customers who have not subscribed.",
                      )}
                      disabled={readonly || !extendedOutreachEnabled}
                      label={t("Non-subscribers / {{productName}}", {
                        productName: "Recover",
                      })}
                      onClick={(event) => event.stopPropagation()}
                      setValue={(value) => setExtendedOutreach(value === true)}
                      size="sm"
                      value={extendedOutreachOn}
                    />
                  )}
                </Flex>
              )}
            </RedoCheckboxCard>
            <RedoCheckboxCard
              checked={channelKind === ShopperAgentChannelKind.Email}
              disabled={readonly}
              inputSize="sm"
              inputType="radio"
              layout={Layout.SHORT}
              onClick={() => setChannelKind(ShopperAgentChannelKind.Email)}
              title={t("Email")}
            >
              {t("Send to customers who have subscribed to email.")}
            </RedoCheckboxCard>
          </Flex>
        </InspectorField>
      </InspectorSection>
      <InspectorSection label={t("Strategy")}>
        <Flex dir="column" gap="sm" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t(
              "Configure guidance for outreach and replies, plus an optional discount.",
            )}
          </Text>
          {!readonly && (
            <RedoButton
              disabled={
                creatingStrategy ||
                !channelValid ||
                otherInvalidSteps.length > 0
              }
              hierarchy="secondary"
              onClick={() => {
                if (isManagedAutomation) {
                  toast(
                    t(
                      "Customize this automation before configuring its {{productName}} strategy.",
                      { productName: "Commerce Agent" },
                    ),
                    { variant: "warning" },
                  );
                  return;
                }
                if (step.strategyId) {
                  openStrategy(step.strategyId);
                } else {
                  void createStrategy();
                }
              }}
              pending={creatingStrategy}
              size="sm"
              text={step.strategyId ? t("Edit strategy") : t("Create strategy")}
            />
          )}
          {otherInvalidSteps.length > 0 && setSelectedId ? (
            <InvalidStepsList
              invalidSteps={otherInvalidSteps}
              onGoToStep={setSelectedId}
            />
          ) : !channelValid ? (
            <Text fontSize="xs" textColor="secondary">
              {t("Select a channel before configuring the strategy.")}
            </Text>
          ) : !step.strategyId ? (
            <Text fontSize="xs" textColor="secondary">
              {t("Add a strategy before this step can start a conversation.")}
            </Text>
          ) : null}
          <RedoButton
            hierarchy="link"
            onClick={() => navigate(routes.getShopperAgentSubRoute("playbook"))}
            size="xs"
            text={t("Playbook settings")}
          />
        </Flex>
      </InspectorSection>
      {/* Keep Always send in step data while its editor control is deferred. */}
      <InspectorSection label={t("Step")}>
        <InspectorField label={null}>
          <RedoToggle
            disabled={readonly}
            label={t("Deactivate")}
            setValue={(disabled) => setStep({ ...step, disabled })}
            size="sm"
            value={step.disabled ?? false}
          />
        </InspectorField>
      </InspectorSection>
    </InspectorLayout>
  );
});

export const validateShopperAgentMessageStep = (
  step: PartialWithRequiredType<ShopperAgentMessageStep>,
): boolean =>
  !!step.strategyId &&
  shopperAgentMessageChannelSchema.safeParse(step.channel).success;

export const shopperAgentMessageStepType: FrontendStepType<
  PartialWithRequiredType<ShopperAgentMessageStep>
> = {
  valid: validateShopperAgentMessageStep,
  type: "custom",
  customInspector: ShopperAgentMessageInspector,
};
